import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { playSound, speak } from '../utils/sounds';
import { getSuccessMessage, getErrorMessage } from '../utils/encouragements';
import RewardCounter from '../components/RewardCounter';

const CountingDownExercise = () => {
  const { level } = useParams();
  const navigate = useNavigate();
  const [currentNumber, setCurrentNumber] = useState(69);
  const [isListening, setIsListening] = useState(false);
  const [recognizedText, setRecognizedText] = useState('');
  const [feedback, setFeedback] = useState(null);
  const [correctCount, setCorrectCount] = useState(0);
  const [incorrectCount, setIncorrectCount] = useState(0);
  const [hasStarted, setHasStarted] = useState(false);
  const recognitionRef = React.useRef(null);
  const isListeningRef = React.useRef(false);
  const currentNumberRef = React.useRef(69);
  const hasSpokenRef = React.useRef(false);
  const validationTimeoutRef = React.useRef(null);

  const minNumber = 0;

  // Convertir un nombre en texte français
  const numberToFrench = (num) => {
    const numbers = {
      0: 'zéro', 1: 'un', 2: 'deux', 3: 'trois', 4: 'quatre', 5: 'cinq',
      6: 'six', 7: 'sept', 8: 'huit', 9: 'neuf', 10: 'dix',
      11: 'onze', 12: 'douze', 13: 'treize', 14: 'quatorze', 15: 'quinze',
      16: 'seize', 17: 'dix-sept', 18: 'dix-huit', 19: 'dix-neuf',
      20: 'vingt', 21: 'vingt-et-un', 22: 'vingt-deux', 23: 'vingt-trois',
      24: 'vingt-quatre', 25: 'vingt-cinq', 26: 'vingt-six', 27: 'vingt-sept',
      28: 'vingt-huit', 29: 'vingt-neuf', 30: 'trente', 31: 'trente-et-un',
      32: 'trente-deux', 33: 'trente-trois', 34: 'trente-quatre', 35: 'trente-cinq',
      36: 'trente-six', 37: 'trente-sept', 38: 'trente-huit', 39: 'trente-neuf',
      40: 'quarante', 41: 'quarante-et-un', 42: 'quarante-deux', 43: 'quarante-trois',
      44: 'quarante-quatre', 45: 'quarante-cinq', 46: 'quarante-six', 47: 'quarante-sept',
      48: 'quarante-huit', 49: 'quarante-neuf', 50: 'cinquante', 51: 'cinquante-et-un',
      52: 'cinquante-deux', 53: 'cinquante-trois', 54: 'cinquante-quatre', 55: 'cinquante-cinq',
      56: 'cinquante-six', 57: 'cinquante-sept', 58: 'cinquante-huit', 59: 'cinquante-neuf',
      60: 'soixante', 61: 'soixante-et-un', 62: 'soixante-deux', 63: 'soixante-trois',
      64: 'soixante-quatre', 65: 'soixante-cinq', 66: 'soixante-six', 67: 'soixante-sept',
      68: 'soixante-huit', 69: 'soixante-neuf'
    };
    return numbers[num] || num.toString();
  };

  // Normaliser le texte reconnu pour comparaison
  const normalizeNumber = (text) => {
    const normalized = text.toLowerCase().trim()
      .normalize('NFD').replace(/[\u0300-\u036f]/g, '') // Enlever les accents
      .replace(/[,.!?]/g, '')
      .replace(/\s+/g, '-');
    return normalized;
  };

  const initSpeechRecognition = useCallback(() => {
    if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      const recog = new SpeechRecognition();
      recog.lang = 'fr-FR';
      recog.continuous = true;
      recog.interimResults = true;
      recog.maxAlternatives = 1;

      recog.onresult = (event) => {
        const lastResult = event.results[event.results.length - 1];
        const transcript = lastResult[0].transcript;
        const spoken = normalizeNumber(transcript);

        console.log('🗣️ Recognized:', transcript, '→', spoken, 'isFinal:', lastResult.isFinal);
        setRecognizedText(transcript);

        // Vérifier si c'est le bon nombre
        const expected = numberToFrench(currentNumberRef.current);
        const normalizedExpected = normalizeNumber(expected);

        // Si c'est un chiffre, vérifier directement avec le nombre attendu
        const isDigit = /^\d+$/.test(transcript.trim());
        const isCorrectDigit = isDigit && parseInt(transcript.trim()) === currentNumberRef.current;

        // Vérifier aussi si c'est le mot français
        const isCorrectWord = spoken === normalizedExpected || spoken.includes(normalizedExpected);

        const isCorrect = isCorrectDigit || isCorrectWord;

        console.log('Comparing:', { spoken, expected, normalizedExpected, isDigit, isCorrectDigit, isCorrectWord, isCorrect });

        const processCorrectAnswer = () => {
          console.log('✅ Correct number!');
          playSound('success');
          setCorrectCount(prev => prev + 1);

          const childName = localStorage.getItem('childName');
          const successMessage = getSuccessMessage(childName);

          setFeedback({ type: 'success', message: `✅ ${currentNumberRef.current}` });

          // Passer au nombre précédent (décompte)
          if (currentNumberRef.current > minNumber) {
            currentNumberRef.current -= 1;
            setCurrentNumber(currentNumberRef.current);
            setRecognizedText('');

            setTimeout(() => {
              setFeedback(null);
            }, 1000);
          } else {
            // Fin de l'exercice
            recog.stop();
            setIsListening(false);
            isListeningRef.current = false;
            speak(`${successMessage} Tu as compté jusqu'à ${minNumber} !`);
            setFeedback({ type: 'success', message: `🎉 Terminé ! Tu as compté jusqu'à ${minNumber} !` });

            setTimeout(() => {
              navigate('/subjects');
            }, 5000);
          }
        };

        if (isCorrect) {
          // Annuler le timeout précédent s'il existe
          if (validationTimeoutRef.current) {
            clearTimeout(validationTimeoutRef.current);
          }

          if (lastResult.isFinal) {
            // Si c'est final, valider immédiatement
            processCorrectAnswer();
          } else {
            // Si c'est intermédiaire mais correct, attendre 800ms pour confirmer
            validationTimeoutRef.current = setTimeout(() => {
              processCorrectAnswer();
            }, 800);
          }
        } else {
          // Si ce n'est pas correct, annuler le timeout
          if (validationTimeoutRef.current) {
            clearTimeout(validationTimeoutRef.current);
            validationTimeoutRef.current = null;
          }
        }
      };

      recog.onerror = (event) => {
        console.error('❌ Speech recognition error:', event.error);
        if (event.error !== 'no-speech' && event.error !== 'aborted') {
          setIsListening(false);
          isListeningRef.current = false;
        }
      };

      recog.onend = () => {
        console.log('🔇 Recognition ended');
        if (isListeningRef.current && currentNumberRef.current >= minNumber) {
          // Redémarrer automatiquement
          try {
            recog.start();
          } catch (e) {
            console.log('Already running');
          }
        } else {
          setIsListening(false);
          isListeningRef.current = false;
        }
      };

      recognitionRef.current = recog;
    }
  }, [navigate]);

  useEffect(() => {
    initSpeechRecognition();

    if (!hasSpokenRef.current) {
      setTimeout(() => {
        speak('Compte de 69 à 0 en reculant. Dis chaque nombre un par un !');
      }, 500);
      hasSpokenRef.current = true;
    }
  }, [initSpeechRecognition]);

  const startCounting = () => {
    playSound('click');
    setHasStarted(true);
    setCurrentNumber(69);
    currentNumberRef.current = 69;
    setRecognizedText('');
    setFeedback(null);

    setTimeout(() => {
      speak('Commence par soixante-neuf !');
      setTimeout(() => {
        if (recognitionRef.current) {
          setIsListening(true);
          isListeningRef.current = true;
          try {
            recognitionRef.current.start();
          } catch (error) {
            console.error('Error starting recognition:', error);
          }
        }
      }, 2000);
    }, 500);
  };

  const handleSkip = () => {
    playSound('click');
    setIncorrectCount(prev => prev + 1);

    const childName = localStorage.getItem('childName');
    const errorMessage = getErrorMessage(childName);
    speak(errorMessage);

    setFeedback({ type: 'error', message: `⏭️ Passé : ${currentNumber}` });

    if (currentNumber > minNumber) {
      currentNumberRef.current -= 1;
      setCurrentNumber(currentNumberRef.current);
      setRecognizedText('');

      setTimeout(() => {
        setFeedback(null);
      }, 1500);
    }
  };

  const handleRestart = () => {
    playSound('click');
    setCurrentNumber(69);
    currentNumberRef.current = 69;
    setRecognizedText('');
    setFeedback(null);
    setCorrectCount(0);
    setIncorrectCount(0);
    setHasStarted(false);

    if (recognitionRef.current && isListening) {
      recognitionRef.current.stop();
      setIsListening(false);
      isListeningRef.current = false;
    }
  };

  return (
    <div>
      <RewardCounter correctCount={correctCount} incorrectCount={incorrectCount} />

      <div className="header">
        <h1>🔢 Apprends à compter de 69 à 0</h1>
        <p>Compte à rebours ! Dis chaque nombre un par un !</p>
      </div>

      <div className="card">
        {!hasStarted ? (
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '5rem', marginBottom: '30px' }}>🔽</div>
            <h2 style={{ fontSize: '2rem', color: '#667eea', marginBottom: '30px' }}>
              Prêt à compter à rebours ?
            </h2>
            <p style={{ fontSize: '1.3rem', marginBottom: '40px', color: '#666' }}>
              Tu vas apprendre à compter de 69 à 0 en reculant
            </p>
            <button
              className="btn btn-primary bounce"
              onClick={startCounting}
              style={{ fontSize: '2rem', padding: '30px 60px' }}
            >
              🚀 Commencer le décompte
            </button>
          </div>
        ) : (
          <>
            <div style={{
              textAlign: 'center',
              fontSize: '8rem',
              fontWeight: 'bold',
              color: '#667eea',
              marginBottom: '30px',
              textShadow: '2px 2px 4px rgba(0,0,0,0.1)'
            }}>
              {currentNumber}
            </div>

            <div style={{
              textAlign: 'center',
              fontSize: '1.5rem',
              color: '#666',
              marginBottom: '20px'
            }}>
              Dis : <strong style={{ color: '#9B51E0' }}>{numberToFrench(currentNumber)}</strong>
            </div>

            {isListening && (
              <div style={{
                textAlign: 'center',
                fontSize: '3rem',
                marginBottom: '20px',
                animation: 'pulse 1s infinite'
              }}>
                🎤
              </div>
            )}

            {recognizedText && (
              <div style={{
                textAlign: 'center',
                fontSize: '1.3rem',
                color: '#2F80ED',
                marginBottom: '20px',
                fontStyle: 'italic'
              }}>
                Tu dis : "{recognizedText}"
              </div>
            )}

            {feedback && (
              <div className={`feedback ${feedback.type === 'success' ? 'feedback-success' : 'feedback-error'}`}
                style={{ fontSize: '1.5rem', marginBottom: '20px' }}>
                {feedback.message}
              </div>
            )}

            <div style={{
              textAlign: 'center',
              marginTop: '40px',
              padding: '15px',
              backgroundColor: '#f0f0f0',
              borderRadius: '10px'
            }}>
              <div style={{ fontSize: '1.2rem', color: '#667eea', fontWeight: 'bold' }}>
                Progression : {69 - currentNumber} / 69
              </div>
              <div style={{
                width: '100%',
                height: '20px',
                backgroundColor: '#ddd',
                borderRadius: '10px',
                marginTop: '10px',
                overflow: 'hidden'
              }}>
                <div style={{
                  width: `${((69 - currentNumber) / 69) * 100}%`,
                  height: '100%',
                  backgroundColor: '#4CAF50',
                  transition: 'width 0.3s ease'
                }} />
              </div>
            </div>

            <div style={{ textAlign: 'center', marginTop: '40px', display: 'flex', gap: '20px', justifyContent: 'center', flexWrap: 'wrap' }}>
              <button
                className="btn btn-warning"
                onClick={handleSkip}
                style={{ fontSize: '1.2rem', padding: '15px 30px' }}
              >
                ⏭️ Passer
              </button>
              <button
                className="btn btn-secondary"
                onClick={handleRestart}
                style={{ fontSize: '1.2rem', padding: '15px 30px' }}
              >
                🔄 Recommencer
              </button>
              <button
                className="btn btn-danger"
                onClick={() => navigate('/subjects')}
                style={{ fontSize: '1.2rem', padding: '15px 30px' }}
              >
                🏠 Retour
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default CountingDownExercise;
